<script>
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import moment from 'moment';
import ProgramCalendar from '@components/programs/program-calendar.vue';
import ProgramSummary from '@/src/components/programs/program-summary.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { workoutModelMixin } from '@mixins/models/workout-model-mixin';
import { cleverTapMixin } from '@mixins/cleverTap-mixin';
import { programMixin } from '@mixins/program-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  workoutMethods,
  clientMethods,
  clientProgramComputed,
  clientProgramMethods,
  exerciseComputed,
  workoutComputed,
  authComputed,
  programComputed,
  sidebarComputed,
  programMethods,
  trainerMethods,
  trainerComputed,
} from '@state/helpers';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';

// could be lazy loaded with magic comments!
const WorkoutPreview = () =>
  import(
    /* webpackChunkName: "CPWorkoutPreview" */ '@components/workout/workout-preview.vue'
  );
const SplitPaneFull = () =>
  import(
    /* webpackChunkName: "CPSplitPaneFull" */ '@components/panels/split-pane-full.vue'
  );
const SplitPane = () =>
  import(
    /* webpackChunkName: "CPSplitPane" */ '@components/panels/split-pane.vue'
  );
const WorkoutDetail = () =>
  import(
    /* webpackChunkName: "CPWorkoutDetail" */ '@components/workout/workout-detail.vue'
  );
const ExerciseList = () =>
  import(
    /* webpackChunkName: "CPExerciseList" */ '@components/exercise/exercise-list.vue'
  );
const WorkoutList = () =>
  import(
    /* webpackChunkName: "CPWorkoutList" */ '@components/workout/workout-list.vue'
  );
const ProgramNote = () =>
  import(
    /* webpackChunkName: "CPProgramNote" */ '@components/note/program-note.vue'
  );
const ActionFooter = () =>
  import(
    /* webpackChunkName: "CPActionFooter" */ '@components/footer/action-footer.vue'
  );
const HeroProgram = () =>
  import(
    /* webpackChunkName: "CPHeroProgram" */ '@components/hero/hero-program.vue'
  );
const CardClientProgress = () =>
  import(
    /* webpackChunkName: "CPCardClientProgress" */ '@components/cards/card-client-progress.vue'
  );

export default {
  page: {
    title: 'Client Program',
    meta: [
      { name: 'description', content: `The Client's Program Detail page.` },
    ],
  },
  props: {
    client: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  components: {
    ActionFooter,
    AdminPanel,
    ExerciseList,
    ProgramNote,
    SplitPane,
    SplitPaneFull,
    HeroProgram,
    ProgramCalendar,
    ProgramSummary,
    WorkoutDetail,
    WorkoutList,
    WorkoutPreview,
    CardClientProgress,
  },
  mixins: [adminPanelMixin, workoutModelMixin, programMixin, cleverTapMixin],
  data() {
    return {
      activateWeekDuplication: false,
      image: require('@assets/images/placeholders/exercise.png'),
      isTrainerModerating: false,
      loadingWeeks: false,
      noteItem: '',
      weeksCount: null,
      programCalendarWidth: null,
      loadingProgramData: false,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...clientProgramComputed,
    ...exerciseComputed,
    ...workoutComputed,
    ...authComputed,
    ...programComputed,
    ...sidebarComputed,
    ...trainerComputed,
    program() {
      return this.client.program;
    },
    isTrainer() {
      return (
        this.currentUser &&
        this.currentUser.roles &&
        this.currentUser.roles.isTrainer
      );
    },
    selectedDayName() {
      if (this.selectedDay && this.selectedDay.dayName) {
        return this.selectedDay.dayName;
      } else if (this.selectedDay && !this.selectedDay.dayName) {
        return this.$_getDayName(this.selectedDay.dayNumber);
      } else {
        return '';
      }
    },
    hasCircuits() {
      return (
        this.selectedWorkout &&
        this.selectedWorkout.circuits &&
        this.selectedWorkout.circuits.length
      );
    },
    panelContentDetail() {
      return (
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.details) ||
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.componentRight &&
          this.panelContent.data.componentRight.showPanelDetail)
      );
    },
  },
  async beforeMount() {
    this.loadingProgramData = true;
    if (this.client) {
      const oldClientObj = this.client;
      if (this.client._id) {
        await this.getClient({ clientObj: this.client })
          .then(async (client) => {
            let newClientObj = cloneDeep(client);
            if (newClientObj) {
              if (oldClientObj && has(oldClientObj, 'hasUnreadMessages')) {
                newClientObj = {
                  ...newClientObj,
                  hasUnreadMessages: oldClientObj.hasUnreadMessages,
                };
              }
              this.setUpdateClientItem(newClientObj);
              this.setSelectedClient(newClientObj);
            }
          })
          .catch((e) => console.log(e));
      }
    }
    if (
      !this.clientActivity ||
      !(
        this.client &&
        this.clientActivity &&
        this.client._id === this.clientActivity.client._id
      )
    ) {
      const lastMondaysDate = moment()
        .startOf('isoWeek')
        .format('YYYY/MM/DD');
      const activityPayload = {
        trainerId: this.trainerId,
        userId: this.client._id,
        mondaysDate: lastMondaysDate,
        trainerToken: this.currentUser.trainerToken,
      };
      await this.getClientActivity(activityPayload);
    }

    if (
      this.client &&
      !(
        this.client &&
        this.selectedProgram &&
        this.client.program.program_id === this.selectedProgram._id
      ) &&
      !(
        this.AllClientProgram &&
        this.AllClientProgram.length > 0 &&
        this.AllClientProgram[0].program_id === this.client.program.program_id
      )
    ) {
      const payload = {
        userId: this.client._id,
        programId: this.client.program.program_id,
        orgId: this.client.org_id,
        trainerId: this.client.trainer_id,
      };
      await this.getProgram(payload);
      this.weeksCount =
        this.selectedProgram && this.selectedProgram.programWeeksNumber
          ? this.selectedProgram.programWeeksNumber + 4
          : 4;

      await this.fetchAllClientProgram();
    } else {
    }
    this.loadingProgramData = false;
    window.addEventListener('resize', this.resizeFooter);
    this.resizeFooter();
  },
  async destroyed() {
    await this.setPanelContent(null);
    window.removeEventListener('resize', this.resizeFooter);
  },
  methods: {
    ...adminPanelMethods,
    ...workoutMethods,
    ...clientMethods,
    ...clientProgramMethods,
    ...programMethods,
    ...trainerMethods,
    resizeFooter() {
      if (this.$refs.programCalendar) {
        this.programCalendarWidth =
          this.$refs.programCalendar.clientWidth - 100;
      }
    },
    async fetchFourMoreWeeks() {
      this.loadingWeeks = true;
      const todaysDate = moment().format('YYYY-MM-DD');
      const programId = this.client.program.program_id;
      const clientProgramPayload = {
        id: programId,
        from: 1,
        to: this.weeksCount,
        today: todaysDate,
      };
      if (
        clientProgramPayload.id &&
        clientProgramPayload.from &&
        clientProgramPayload.to &&
        clientProgramPayload.today
      ) {
        await this.getAllClientProgram(clientProgramPayload)
          .then((res) => {
            if (res && res.success) {
              this.weeksCount += 4;
              this.loadingWeeks = false;
            }
          })
          .catch((e) => console.log(e));
      }
    },
    async showAdminPanel(component, data = null, size = '') {
      if (component === 'ExerciseList') {
        await this.setPanelContent({
          ...this.getPanelContent(component, data, size),
          title: `Day ${this.selectedDay.calculatedDayNumber}: Add New Workout`,
        });
      } else {
        await this.setPanelContent({
          ...this.getPanelContent(component, data, size),
          workoutPreviewType: data.workoutPreviewType
            ? data.workoutPreviewType
            : null,
        });
      }
      this.setShowAdminPanel();
    },
    async onAddWorkout(day, week) {
      this.isTrainerModerating = true;
      this.setIsLibraryWorkout(false);
      this.setInitialLibraryWorkout(null);
      const workout = await this.getWorkoutModel();
      workout.isCustom = true;
      workout.programWorkout_id = null;
      workout.trainer_id = this.trainerId;
      workout.org_id = this.userOrgId;
      await this.setSelectedWorkout(workout);
      await this.setSelectedWeek(week);
      await this.setSelectedDay(day);

      const panelContent = {
        panelTitle: 'Create Workout',
        componentLeft: {
          name: 'WorkoutDetail',
          selectedWorkout: this.selectedWorkout,
          isNewWorkout: true,
          inPanel: true,
        },
        componentRight: {
          name: null,
          hideDescription: true,
          showExerciseFilters: true,
        },
      };
      this.showAdminPanel('SplitPaneFull', panelContent, 'split-lg');
    },
    createNote() {},
    async viewNote(day, week) {
      if (day.programDay_id) {
        await this.getNote(day, week)
          .then((note) => {
            if (note !== null) {
              this.noteItem = note;
              this.showAdminPanel(
                'ProgramNote',
                {
                  title: `Day ${this.getDayNumber(
                    week.weekNumber,
                    day.dayNumber
                  )}: Note`,
                  day,
                  week,
                },
                null
              );
            }
          })
          .catch((e) => console.log(e));
      } else {
        this.showAdminPanel(
          'ProgramNote',
          {
            title: `Day ${this.getDayNumber(
              week.weekNumber,
              day.dayNumber
            )}: Note`,
            day,
            week,
          },
          null
        );
      }
    },
    async getNote(day, week) {
      const dayId = day.programDay_id;
      const trainerId = week.trainer_id;
      const orgId = week.org_id;
      const userId = week.user_id;
      const payload = { dayId, trainerId, orgId, userId };

      return this.getAllDay(payload)
        .then((res) => {
          if (res && res.success) {
            return res.programDay.trainerNote;
          } else {
            return null;
          }
        })
        .catch((e) => console.log(e));
    },
    getDayNumber(weekNumber, dayNumber) {
      return (weekNumber - 1) * 7 + dayNumber;
    },
    async clientWorkoutView(workout, day, week) {
      const dayId = day.programDay_id;
      const trainerId = week.trainer_id;
      const orgId = week.org_id;
      const userId = week.user_id;
      const payload = { dayId, trainerId, orgId, userId };

      if (dayId && trainerId && orgId && userId) {
        await this.getAllDay(payload)
          .then((res) => {
            if (res && res.success && res.programDay.workouts.length > 0) {
              const workout = res.programDay.workouts[0];
              this.setSelectedWorkout(workout);
              this.setSelectedWeek(week);
              this.setSelectedDay(day);
              const panelContent = {
                workoutPreviewType: 'trainer',
              };
              this.showAdminPanel('WorkoutPreview', panelContent, null);
              const client = this.client.email;
              const trainer = this.trainerEmail;
              this.$_pushCleverTapEvent('Review Workout Feedback2', {
                client,
                trainer,
              });
            }
          })
          .catch((e) => console.log(e));
      }
    },
    async trainerWorkoutView(workout, day, week) {
      this.isTrainerModerating = true;
      this.setIsLibraryWorkout(false);
      this.setInitialLibraryWorkout(null);
      const dayId = day.programDay_id;
      const trainerId = week.trainer_id;
      const orgId = week.org_id;
      const userId = week.user_id;
      const payload = { dayId, trainerId, orgId, userId };

      if (dayId && trainerId && orgId && userId) {
        await this.getAllDay(payload)
          .then((res) => {
            if (res && res.success && res.programDay.workouts.length > 0) {
              const workout = res.programDay.workouts[0];
              this.setSelectedWorkout(workout);
              this.setSelectedWeek(week);
              this.setSelectedDay(res.programDay);
              const panelContent = {
                panelTitle: 'Edit Workout',
                componentLeft: {
                  name: 'WorkoutDetail',
                  selectedWorkout: this.selectedWorkout,
                  inPanel: true,
                },
                componentRight: {
                  name: 'ExerciseList',
                  hideDescription: true,
                  showExerciseFilters: true,
                },
              };
              this.showAdminPanel('SplitPaneFull', panelContent, 'split-lg');
            }
          })
          .catch((e) => console.log(e));
      }
    },
    onViewWorkout(workout, day, week) {
      const isCompletedOrStarted = !!(
        workout.clientInput &&
        (workout.clientInput.completedAt || workout.clientInput.startedAt)
      );
      if (!isCompletedOrStarted && this.isTrainer) {
        this.trainerWorkoutView(workout, day, week);
      } else {
        this.clientWorkoutView(workout, day, week);
      }
    },
    onHidePanel() {
      // Check changes when panel is not full (on click away from closing the panel and lose changes)
      if (
        (this.hasUnsavedProgramChanges || this.hasUnsavedChanges) &&
        !this.panelContentDetail
      ) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      } else {
        if (this.isTrainerModerating) {
          this.setIsLibraryWorkout(false);
          this.setInitialLibraryWorkout(null);

          if (
            this.panelContent &&
            this.panelContent.data &&
            this.panelContent.data.componentRight &&
            this.panelContent.data.componentRight.showPanelDetail
          ) {
            this.setHidePanelContentDetail();
          } else {
            this.setSelectedWorkout(null);
          }
        } else {
          this.setSelectedWorkout(null);
        }
      }
    },
    async fetchAllClientProgram() {
      const todaysDate = moment().format('YYYY-MM-DD');
      const programId = this.client.program.program_id;
      const clientProgramPayload = {
        id: programId,
        from: 1,
        to: this.selectedProgram.programWeeksNumber
          ? this.selectedProgram.programWeeksNumber
          : 4,
        today: todaysDate,
      };

      if (
        clientProgramPayload.id &&
        clientProgramPayload.from &&
        clientProgramPayload.to &&
        clientProgramPayload.today
      ) {
        await this.getAllClientProgram(clientProgramPayload);
      }
    },
    async onClearAllWeekEvents() {
      const payload = {
        programId: this.selectedAllWeek.program_id,
        weekId: this.selectedAllWeek._id,
      };
      await this.clearAllWeekEvents(payload)
        .then((res) => {
          this.deselectWeek();
        })
        .catch((e) => console.log(e));
    },
    scrollUp() {
      const calendarTopPost = document.getElementsByClassName(
        'content-wrapper'
      )[0].scrollTop;
      document.getElementsByClassName('content-wrapper')[0].scrollTo({
        top: calendarTopPost - 150,
        behavior: 'smooth',
      });
    },
    scrollDown() {
      const calendarTopPost = document.getElementsByClassName(
        'content-wrapper'
      )[0].scrollTop;
      document.getElementsByClassName('content-wrapper')[0].scrollTo({
        top: calendarTopPost + 150,
        behavior: 'smooth',
      });
    },
    setActivateWeekDuplication(val) {
      this.activateWeekDuplication = val;
    },
    deselectWeek() {
      if (this.selectedAllWeek && this.selectedAllWeek.weekOf) {
        const prevCheck = document.getElementById(this.selectedAllWeek.weekOf);
        prevCheck.checked = false;
      }
      this.setSelectedAllWeek(null);
      this.setActivateWeekDuplication(false);
    },
    showWorkoutListPanelContent(component) {
      this.setPanelContent({
        ...this.panelContent,
        data: {
          ...this.panelContent.data,
          panelTitle: `Add Workout to ${this.selectedDayName}`,
          componentRight: {
            name: component,
            showPanelDetail: true,
            showWorkoutFilters: true,
          },
        },
      });
    },
    async expandPanel(component, selectedCircuitIndex = null) {
      switch (component) {
        case 'WorkoutList':
          this.showWorkoutListPanelContent(component);
          break;
        default:
          break;
      }
    },
  },
  watch: {
    isSidebarCollapsed() {
      setTimeout(() => {
        this.resizeFooter();
      }, 220);
    },
  },
};
</script>

<template>
  <div
    ref="programCalendar"
    :class="[{ adminPanelClosed: !isAdminPanelActive }]"
  >
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :split="
        panelContent && panelContent.data && panelContent.data.split === true
      "
      :noPad="true"
      @hide-panel="onHidePanel"
    >
      <template
        v-slot:header
        v-if="
          !hasCircuits &&
            panelContent &&
            panelContent.data &&
            panelContent.data.componentLeft &&
            panelContent.data.componentLeft.name === 'WorkoutDetail'
        "
      >
        <span class="workout-btn">
          <span>or</span>
          <BaseButton
            type="button"
            class="btn-secondary btn-round"
            @click="expandPanel('WorkoutList')"
          >
            Start From Workout Library
          </BaseButton>
        </span>
      </template>
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
        :workout="selectedWorkout"
        :hideDescription="true"
        :showExerciseFilters="panelContent.data.showExerciseFilters"
        :inPanel="true"
        :noteItem="noteItem"
      />
    </AdminPanel>
    <div v-if="!loadingProgramData">
      <!-- <HeroProgram
        :title="program.label"
        :description="program.description"
        :client="client"
      /> -->
      <ProgramSummary :client="client" :programWeeks="AllClientProgram" />
      <!-- <CardClientProgress
        :goalCharts="clientActivity.goalCharts"
        :client="clientActivity.client"
      /> -->
      <ProgramCalendar
        @add-workout="onAddWorkout"
        @view-workout="onViewWorkout"
        @create-note="createNote"
        @view-note="viewNote"
        @set-activate-week-duplication="setActivateWeekDuplication"
        :activateWeekDuplication="activateWeekDuplication"
        :isClientProgram="true"
        :programWeeks="AllClientProgram"
        :allowEditing="true"
      />
      <div class="show-more-weeks">
        <BaseButton
          v-if="client"
          type="button"
          class="btn-secondary"
          @click="fetchFourMoreWeeks"
          :disabled="loadingWeeks"
          ><BaseIcon v-if="loadingWeeks" name="sync" spin />
          <span v-if="!loadingWeeks">Show 4 more weeks</span>
        </BaseButton>
      </div>
      <transition name="slide-fade">
        <ActionFooter
          v-if="selectedAllWeek"
          :color="selectedAllWeek ? 'blue' : 'black'"
          :style="
            `${!isAdminPanelActive ? `width: ${programCalendarWidth}px` : ''}`
          "
        >
          <span v-if="!selectedAllWeek" class="saved-status" slot="actionState">
            Last Saved: April 19th at 12:32pm
          </span>
          <span v-if="selectedAllWeek" slot="secondaryAction">
            Week {{ selectedAllWeek.weekNumber }}
            <span class="link-deselect" @click="deselectWeek">
              Deselect
            </span>
          </span>
          <BaseButton
            v-if="!selectedAllWeek"
            type="button"
            class="btn-primary btn-clear"
            @click="scrollUp"
          >
            <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-arrow'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1.178 6.519l5.196-4.935 5.196 4.935" stroke="#030C16" stroke-width="2"/><path d="M6.44 3.416v10" stroke="#030C16" stroke-width="2" stroke-linecap="square"/></svg>
            Move Up
          </BaseButton>
          <span class="divider-vertical">&nbsp;</span>
          <BaseButton
            v-if="!selectedAllWeek"
            type="button"
            class="btn-primary btn-clear"
            @click="scrollDown"
          >
            <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-arrow'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1.608 8.481l5.196 4.935L12 8.481" stroke="#030C16" stroke-width="2"/><path d="M6.871 11.584v-10" stroke="#030C16" stroke-width="2" stroke-linecap="square"/></svg>
            Move Down
          </BaseButton>
          <BaseButton
            v-if="!selectedAllWeek"
            type="button"
            class="btn-primary btn-clear"
            @click="scrollDown"
          >
            <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'btn-icon icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
            Remove {{ client.profile.firstName }}'s Program
          </BaseButton>
          <BaseButton
            v-if="selectedAllWeek"
            type="button"
            class="btn-primary btn-clear"
            @click="onClearAllWeekEvents"
          >
            <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'btn-icon icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
            Clear Week
          </BaseButton>
          <span v-if="selectedAllWeek" class="divider-vertical">&nbsp;</span>
          <BaseButton
            v-if="selectedAllWeek"
            type="button"
            class="btn-primary btn-clear"
            @click="setActivateWeekDuplication(true)"
          >
            <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'btn-icon icon-copy'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.876.4H2.296C1.053.4.034 1.43.034 2.69v16.037h2.264V2.691h13.579V.4zm3.394 4.582H6.823c-1.245 0-2.263 1.03-2.263 2.29V23.31c0 1.26 1.018 2.291 2.263 2.291H19.27c1.245 0 2.264-1.03 2.264-2.29V7.272c0-1.26-1.019-2.291-2.264-2.291zM6.823 23.309H19.27V7.273H6.823v16.036z" fill="#2F70DE"/></svg>
            Duplicate Week
          </BaseButton>
        </ActionFooter>
      </transition>
    </div>
    <div class="loading-wrapper" v-if="loadingProgramData">
      <BaseLoadingSpinner />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.loading-wrapper {
  align-items: center;
  display: flex;
  height: calc(100vh - 10.6rem);
  justify-content: center;
  margin-bottom: auto;
  margin-top: auto;
  width: 100%;
  z-index: 100;
}

::v-deep .workout-detail {
  .action-footer-wrapper {
    padding-right: 6rem;
  }
}
.show-more-weeks {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 16rem;
  padding-top: 2rem;
  position: relative;
}

.slide-fade-enter-active {
  transform: translateY(0%);
  transition: all $duration-animation-base ease;
}
.slide-fade-leave-active {
  transform: translateY(0%);
  transition: all $duration-animation-base ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.adminPanelClosed {
  ::v-deep .action-footer-wrapper {
    bottom: 3rem;
    padding: unset;
    right: 6rem;
    z-index: 5;
    @media screen and (max-width: 1024px) {
      padding-right: unset;
    }
  }
}
.icon-arrow {
  margin-right: 0.6rem;
  path {
    stroke: white;
  }
}
.icon-copy {
  height: 18px;
  margin-right: 0.8rem;
  width: 14px;
  path {
    fill: white;
  }
}
.btn-primary {
  &:hover {
    opacity: 0.8;
  }
}

.panel-footer {
  bottom: 5rem;
}

::v-deep .workout-preview-content {
  padding-bottom: 15rem !important;
}
::v-deep .workout-preview {
  padding-bottom: 10rem;
  .panel-footer {
    bottom: 2rem;
  }
}

::v-deep .split-pane {
  .workout-filters {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
    .search-input-wrapper {
      width: 100%;
    }
    .muscle_group {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .level,
    .intensity {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
  .list-item {
    padding-left: 2rem;
  }
}

::v-deep .pane-right {
  padding-bottom: 10rem !important;
}
</style>
